/* =====================================================================
   AstaLab 2.0 — SupabaseCatalogRepository (round alpha.2, obiettivo 9-11)

   Implementazione reale di CatalogRepository sopra Supabase. Le
   operazioni di SOLA LETTURA usano direttamente PostgREST (select),
   protette dalle policy RLS di 0007_catalog_rls_grants.sql (SELECT per
   qualunque authenticated, dato globale — mai filtrato per proprietario,
   vedi ARCHITECTURE_2.md "Dati globali vs dati dell'asta"). Le
   operazioni di SCRITTURA (import listone/statistiche/esterne,
   associazioni) passano SEMPRE da una RPC SECURITY DEFINER
   (backend/migrations/0008_catalog_rpc_import.sql), che verifica
   internamente private.is_admin() — questo file non decide MAI da solo
   se un'operazione è permessa (stesso principio di SupabaseBackendAdapter):
   se l'utente non è admin, la RPC solleva un'eccezione Postgres e questo
   adapter la traduce in { ok:false, error }, mai in un successo silenzioso.

   Nessun algoritmo di riconciliazione/copertura è riscritto qui: per le
   letture aggregate (copertura, dati mancanti, statistiche di un
   giocatore) questo file RESHAPE le righe Postgres nella stessa forma
   `catalogState` usata da ArtifactDemoCatalogRepository e poi chiama le
   STESSE funzioni pure di statisticsEngine.js — mai una copia
   dell'algoritmo (obiettivo 11).

   Revisione r1 (audit): le RPC di scrittura esterne (import/associa/rimuovi/attiva, 0008) ora
   richiamano tutte private.reconcile_external_records() — un reconciler lato Postgres equivalente
   ai 5 esiti di externalRecordsEngine.resolveExternalRecordPlayer() (matched/unmatched/ambiguous/
   identity-conflict, con canonicalizzazione del fantacalcio_id in caso di match via externalId).

   Revisione r2 (audit, correzione allo stesso 0008): il limite descritto sopra nella r1 (il
   reconciler SQL non faceva un merge campo-per-campo dei record duplicati, ne sopravviveva uno
   solo) è stato RISOLTO — private.reconcile_external_records() ora fa un vero merge campo-per-campo
   (private.merge_external_numeric_field/merge_external_text_field), mai una perdita di dati per un
   aggiornamento legittimo della stessa fonte esterna, esattamente come mergeExternalRecordGroup()
   lato JS (externalRecordsEngine.js). Revisione r3 (terzo audit): la politica di merge cumulativo
   dei due backend è stata resa IDENTICA (un valore che aumenta è un aggiornamento legittimo, uno
   che diminuisce è una regressione registrata, mai un silenzio) — vedi ARCHITECTURE_2.md per il
   dettaglio completo della parità JS/SQL raggiunta in questa revisione (blocker 1/2/6/7/8/9/10).
   ===================================================================== */
import { createClient } from "@supabase/supabase-js";
import { CatalogRepository } from "./CatalogRepository.js";
import {
  computeMultiseasonCoverage, computeMissingDataRows, getPlayerSeasonStats, getStatisticsTargetSeasons,
  validateListoneApplyContract, validateStatisticsApplyContract
} from "./statisticsEngine.js";
import { setExternalIdEntry, isVendorScopedExternalSource } from "./externalRecordsEngine.js";
import { canonicalizeProviderKey, validateProviderKeyFormat } from "./providerEnrichmentEngine.js";

function mapRpcError(error){
  if(!error) return null;
  const msg = String(error.message || error);
  if(/solo un amministratore/i.test(msg)) return msg;
  if(msg.includes("already associated") || /già associato/i.test(msg)) return msg;
  return msg;
}

/** Riga record listone (parseQuotazioniRows) → payload atteso da import_catalog_snapshot() (0008).
 *  Funzione pura, esportata per essere testabile senza un client Supabase reale. */
export function mapListoneRecordToRpcPayload(r){
  return {
    fantacalcioId: r.fantacalcioId, nome: r.nome, squadra: r.squadra,
    ruolo: r.ruolo, ruoloMantra: r.ruoloMantra || "", qtA: r.qtA, qtI: r.qtI, fvm: r.fvm
  };
}

/** Riga record statistiche (parseStatisticheRows/normalizeStatisticsRecord) → payload atteso da
 *  import_statistics_archive() (0008). Funzione pura, esportata per essere testabile senza un
 *  client Supabase reale.
 *
 *  Revisione r1 (audit, blocker 2): "espulsioni" mancava qui — la RPC riceveva un payload SENZA
 *  quel campo e scriveva sempre red_cards=NULL, perdendo silenziosamente il dato in ogni import
 *  cloud. Ogni campo qui elencato DEVE avere una colonna corrispondente letta da
 *  import_statistics_archive() in 0008 — vedi tests/test_catalog_rpc_contract.js per un controllo
 *  automatico che eviti la stessa classe di bug in futuro. */
export function mapStatisticsRecordToRpcPayload(r){
  return {
    fantacalcioId: r.fantacalcioId, squadra:r.squadra, ruolo:r.ruolo,
    pv:r.pv, mv:r.mv, fm:r.fm, gf:r.gf, gs:r.gs, rp:r.rp, rc:r.rc,
    rigoriSegnati:r.rigoriSegnati, rigoriSbagliati:r.rigoriSbagliati, assist:r.assist,
    ammonizioni:r.ammonizioni, espulsioni:r.espulsioni, autogol:r.autogol
  };
}

/** Righe `players` Postgres → stessa forma usata dal motore JS (catalogSnapshotEngine/statisticsEngine).
 *  Esportata (revisione r4, blocker 7) per essere verificabile da un test di contratto senza un
 *  client Supabase reale. */
export function mapPlayerRow(row){
  return {
    id: row.id,
    fantacalcioId: row.fantacalcio_id,
    nome: row.name,
    squadra: row.current_team,
    ruolo: row.classic_role,
    ruoloMantra: row.mantra_role,
    quotazione: row.quotation,
    fvm: row.fvm,
    current: row.current,
    removedAt: row.removed_at,
    historicalAliases: [],
    externalIds: {},
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

/** Riga `external_statistics` Postgres (snake_case, RESA DIRETTAMENTE da PostgREST) → contratto
 *  canonico camelCase condiviso con ArtifactDemoCatalogRepository/CatalogRepository/la UI
 *  (CatalogPage.js legge r.fantacalcioId/r.sourcePlayerId/r.season, MAI gli snake_case Postgres).
 *  Revisione r4 (audit, blocker 1): PRIMA di questa revisione listExternalRecords()/
 *  listUnassociatedExternalRecords() restituivano le righe raw PostgREST senza alcun mapping —
 *  il deploy reale avrebbe quindi mostrato record esterni vuoti/rotti in UI (ogni lettura di
 *  r.fantacalcioId su una riga con solo fantacalcio_id restituisce undefined). Funzione pura,
 *  esportata per essere testabile senza un client Supabase reale. */
export function mapExternalStatisticsRow(row){
  return {
    id: row.id,
    fantacalcioId: row.fantacalcio_id,
    playerId: row.player_id,
    nome: row.name,
    season: row.season_key,
    source: row.source,
    sourcePlayerId: row.source_player_id,
    sourceUrl: row.source_url,
    team: row.team,
    competition: row.competition,
    appearances: row.appearances,
    starts: row.starts,
    minutes: row.minutes,
    goals: row.goals,
    assists: row.assists,
    yellowCards: row.yellow_cards,
    redCards: row.red_cards,
    penaltiesScored: row.penalties_scored,
    penaltiesMissed: row.penalties_missed,
    cleanSheets: row.clean_sheets,
    goalsConceded: row.goals_conceded,
    notes: row.notes,
    active: row.active,
    importedAt: row.imported_at,
    // Round alpha.3-r1 (0010_provider_observation_versioning.sql) — sempre presenti nella riga
    // Postgres (NULL per ogni fonte csv-manual/transfermarkt-manual), esposti qui per lo stesso
    // identico contratto normalizzato di ArtifactDemoCatalogRepository (normalizeExternalStatisticsRecord()).
    providerKey: row.provider_key,
    providerObservationId: row.provider_observation_id,
    providerObservedAt: row.provider_observed_at,
    providerPayloadChecksum: row.provider_payload_checksum,
    // Revisione alpha.3-r3 (0010, audit blocker A) — parità di contratto con
    // ArtifactDemoCatalogRepository (assignReceiptSequences()); il valore reale è sempre calcolato
    // server-side da import_external_statistics() (mai fidato dal client), esposto qui solo in
    // lettura per diagnostica/test di contratto.
    receiptSeq: row.receipt_seq
  };
}

/** Riga `statistics_conflicts` Postgres (round 2.2.0) → contratto camelCase per la UI di
 *  risoluzione admin-only. Deliberatamente distinto dal campo `statisticsConflicts` (sempre vuoto)
 *  restituito da _loadCatalogStateForCoverage() sotto — quel campo appartiene alla simulazione
 *  client-side di anteprima import (statisticsEngine.js), mai al registro persistito letto qui. */
export function mapStatisticsConflictRow(row){
  return {
    id: row.id,
    fantacalcioId: row.fantacalcio_id,
    seasonKey: row.season_key,
    field: row.field,
    existingValue: row.existing_value,
    incomingValue: row.incoming_value,
    sourceFile: row.source_file,
    detectedAt: row.detected_at
  };
}

/** Riga `unassociated_external_statistics` Postgres → stesso contratto canonico di
 *  mapExternalStatisticsRow(), più `status`/`reason` (coda "non associati" — mai un giocatore
 *  risolto, quindi `playerId` resta sempre null e `active` sempre false, esattamente come
 *  ArtifactDemoCatalogRepository.listUnassociatedExternalRecords()). */
export function mapUnassociatedExternalStatisticsRow(row){
  return {
    id: row.id,
    fantacalcioId: row.fantacalcio_id,
    playerId: null,
    nome: row.name,
    season: row.season_key,
    source: row.source,
    sourcePlayerId: row.source_player_id,
    sourceUrl: row.source_url,
    team: row.team,
    competition: row.competition,
    appearances: row.appearances,
    starts: row.starts,
    minutes: row.minutes,
    goals: row.goals,
    assists: row.assists,
    yellowCards: row.yellow_cards,
    redCards: row.red_cards,
    penaltiesScored: row.penalties_scored,
    penaltiesMissed: row.penalties_missed,
    cleanSheets: row.clean_sheets,
    goalsConceded: row.goals_conceded,
    notes: row.notes,
    active: false,
    importedAt: row.imported_at,
    status: row.status,
    reason: row.reason,
    providerKey: row.provider_key,
    providerObservationId: row.provider_observation_id,
    providerObservedAt: row.provider_observed_at,
    providerPayloadChecksum: row.provider_payload_checksum,
    receiptSeq: row.receipt_seq
  };
}

/** Riga `player_season_stats` Postgres → forma canonica camelCase condivisa da OGNI lettore delle
 *  statistiche (buildStatisticsArchivesFromRows() sotto, e la query mirata per-giocatore di
 *  getPlayerSeasonStats() — round alpha.7 A7-P1-1: mai una seconda mappatura divergente). Estratta
 *  come funzione dedicata così che una nuova colonna/rinomina venga corretta in UN solo punto. */
export function mapPlayerSeasonStatsRow(r){
  return {
    fantacalcioId: r.fantacalcio_id, squadra:r.team, ruolo:r.role,
    pv:r.pv, mv:r.mv, fm:r.fm, gf:r.gf, gs:r.gs, rp:r.rp, rc:r.rc,
    rigoriSegnati:r.penalties_scored, rigoriSbagliati:r.penalties_missed,
    assist:r.assists, ammonizioni:r.yellow_cards, espulsioni:r.red_cards, autogol:r.own_goals,
    presenze:r.appearances, titolare:r.starts, minuti:r.minutes
  };
}

/** Righe `player_season_stats` Postgres, raggruppate per season_key → forma statisticsArchives
 *  (`{[seasonKey]: {label, players:{[fantacalcioId]: {...}}}}`) usata dal fallback ad archivio
 *  completo/alias di getPlayerSeasonStats(). Esportata (revisione r4, blocker 7) per essere
 *  verificabile da un test di contratto senza un client Supabase reale. */
export function buildStatisticsArchivesFromRows(rows){
  const archives = {};
  rows.forEach(r=>{
    if(!archives[r.season_key]) archives[r.season_key] = { label:r.season_key, players:{} };
    archives[r.season_key].players[r.fantacalcio_id] = mapPlayerSeasonStatsRow(r);
  });
  return archives;
}

/** Colonne di public.player_season_stats effettivamente richieste da buildStatisticsArchivesFromRows()
 *  — mai select("*"): un select esplicito rende impossibile rompere silenziosamente il mapping
 *  aggiungendo in futuro una colonna che cambierebbe la "forma" implicita di un select(*) senza che
 *  nessuno se ne accorga. */
const PLAYER_SEASON_STATS_COVERAGE_COLUMNS =
  "id, fantacalcio_id, season_key, source_type, team, role, pv, mv, fm, gf, gs, rp, rc, " +
  "penalties_scored, penalties_missed, assists, yellow_cards, red_cards, own_goals, " +
  "appearances, starts, minutes";

const SUPABASE_PAGE_SIZE = 1000;

/** Revisione r10 (audit del gate reale, root cause): legge TUTTE le righe di
 *  public.player_season_stats (opzionalmente filtrate per season_key), paginando con .range() finché
 *  una pagina non restituisce ZERO righe — MAI un'unica select() non paginata. Supabase/PostgREST
 *  limita silenziosamente le risposte della Data API a un massimo configurato di righe (default
 *  1000): un dataset con più righe di quel limite (le fixture reali producono 663+679=1342 righe
 *  statistiche ufficiali) veniva quindi letto SOLO PARZIALMENTE, facendo calcolare una copertura
 *  completamente errata (il gate reale dopo 0009 falliva esattamente qui: "both" atteso 272,
 *  effettivo 0 — il database era corretto, il client leggeva un dataset troncato).
 *
 *  FAIL-CLOSED: un errore PostgREST su QUALUNQUE pagina interrompe l'intera lettura e propaga
 *  un'eccezione esplicita — mai un dataset parziale silenziosamente accettato come "completo".
 *
 *  ORDER BY deterministico (season_key, fantacalcio_id, source_type — esattamente la chiave UNIQUE
 *  della tabella, vedi 0005_catalog_schema.sql: player_season_stats_unique) — necessario perché
 *  .range() su un risultato non ordinato in modo stabile potrebbe restituire righe duplicate o
 *  perse fra una pagina e l'altra.
 *
 *  L'offset avanza del NUMERO DI RIGHE REALMENTE ricevute in ciascuna pagina, mai di un +=PAGE_SIZE
 *  fisso: il limite lato server potrebbe essere inferiore alla pagina richiesta (max_rows del
 *  progetto), e un incremento fisso in quel caso salterebbe silenziosamente delle righe. La
 *  condizione di arresto è "una pagina ha restituito zero righe", mai "una pagina più corta della
 *  dimensione richiesta" — quest'ultima si romperebbe se il server avesse un max_rows diverso da
 *  SUPABASE_PAGE_SIZE.
 *
 *  Esportata come funzione dedicata (non un metodo privato) cosi' da essere testabile con un client
 *  PostgREST simulato, senza costruire un SupabaseCatalogRepository reale — vedi
 *  tests/test_supabase_pagination_r10.js. */
export async function loadAllPlayerSeasonStatRows(client, seasonKeys){
  const rows = [];
  let offset = 0;
  while(true){
    let query = client.from("player_season_stats").select(PLAYER_SEASON_STATS_COVERAGE_COLUMNS)
      .order("season_key", { ascending:true })
      .order("fantacalcio_id", { ascending:true })
      .order("source_type", { ascending:true });
    if(seasonKeys && seasonKeys.length) query = query.in("season_key", seasonKeys);
    query = query.range(offset, offset + SUPABASE_PAGE_SIZE - 1);

    const { data, error } = await query;
    if(error){
      throw new Error("Impossibile caricare completamente player_season_stats: " + (error.message || error));
    }
    if(!data || data.length === 0) break;
    rows.push(...data);
    offset += data.length;
  }
  return rows;
}

export class SupabaseCatalogRepository extends CatalogRepository{
  /**
   * @param {string} supabaseUrl
   * @param {string} supabasePublishableKey
   */
  constructor(supabaseUrl, supabasePublishableKey){
    super();
    if(!supabaseUrl || !supabasePublishableKey){
      throw new Error("SupabaseCatalogRepository richiede sempre supabaseUrl e supabasePublishableKey (mai una service_role key).");
    }
    this._client = createClient(supabaseUrl, supabasePublishableKey);
  }

  get kind(){ return "supabase"; }

  // Round 2.2.0: SOLA comodità UI (mostrare/nascondere la sezione di gestione conflitti) — mai il
  // confine di sicurezza reale, che resta sempre private.is_admin() dentro resolveStatisticsConflict
  // sotto. Legge profiles.is_admin della PROPRIA riga (RLS profiles_select_own, 0003 — nessun nuovo
  // GRANT/migrazione necessario), fail-closed a false su qualunque errore/sessione assente/riga
  // mancante (mai un default permissivo).
  async isCurrentUserAdmin(){
    const { data: userData, error: userError } = await this._client.auth.getUser();
    if(userError || !userData || !userData.user) return false;
    const { data, error } = await this._client.from("profiles").select("is_admin").eq("id", userData.user.id).maybeSingle();
    if(error || !data) return false;
    return !!data.is_admin;
  }

  async listCurrentPlayers(){
    const { data, error } = await this._client.from("players").select("*").eq("current", true).order("name");
    if(error || !data) return [];
    return data.map(mapPlayerRow);
  }

  async getPlayer(playerId){
    const { data, error } = await this._client.from("players").select("*").eq("id", playerId).maybeSingle();
    if(error || !data) return null;
    const player = mapPlayerRow(data);
    // Revisione alpha.3-r3 (0010, audit blocker B): PRIMA di questa revisione ogni riga veniva
    // scritta come scalare `player.externalIds[r.source] = r.external_player_id` — con più righe
    // "provider-authorized" per lo stesso giocatore (un vendor per riga, dopo blocker B), l'ULTIMA
    // letta sovrascriveva silenziosamente le precedenti. setExternalIdEntry() (stessa funzione pura
    // usata da ArtifactDemoCatalogRepository) fa convergere entrambi i repository sulla STESSA forma:
    // scalare per csv-manual/transfermarkt-manual, oggetto {providerKey: valore} per
    // provider-authorized (nessuna associazione vendor persa).
    const { data: extIds } = await this._client.from("external_player_ids").select("source, external_player_id, provider_key").eq("player_id", playerId);
    (extIds||[]).forEach(r=>{ setExternalIdEntry(player, r.source, isVendorScopedExternalSource(r.source) ? r.provider_key : null, r.external_player_id); });
    const { data: aliases } = await this._client.from("player_historical_aliases").select("alias_name, alias_team, season_key").eq("player_id", playerId);
    // Revisione r5 (audit, blocker 8): il contratto canonico usato da
    // statisticsEngine.getPlayerSeasonStats() (`a.season===seasonKey`) e dalle fixture di test
    // (test_catalog_coverage.js) è `season`, MAI `seasonKey` — prima di questa correzione un alias
    // storico reale non veniva MAI trovato dal motore statistiche contro un deploy Supabase
    // (a.seasonKey esisteva ma a.season era sempre undefined, quindi il confronto falliva sempre).
    player.historicalAliases = (aliases||[]).map(a=>({nome:a.alias_name, squadra:a.alias_team, season:a.season_key}));
    return player;
  }

  /**
   * @param {Array} records - righe normalizzate da parseQuotazioniRows() (vedi importPreview.buildListoneUpdatePreview)
   * @param {string} sourceFile
   * @param {{seasonKey:string,label:string}|null} seasonInfo
   */
  // Revisione r4 (audit, blocker 9): syntheticTestRunId è un parametro OPZIONALE, mai passato dalla
  // UI applicativa (solo tests/real-supabase/test_catalog_real.js lo valorizza per le proprie
  // sezioni sintetiche) — permette a admin_purge_synthetic_test_data() di ripulire correttamente
  // anche le righe di audit catalog_imports di un listone sintetico (season_key sempre REALE, es.
  // "2026_27" — un listone mescola dati reali e sintetici, mai distinguibile per season_key da solo).
  async applyListoneSnapshot(records, sourceFile, seasonInfo, fileChecksum, syntheticTestRunId){
    // Revisione r7 (audit, gap di parità repository Artifact/Supabase): la RPC (0008) resta il
    // backstop AUTOREVOLE — questa validazione è un fail-fast PRIMA di qualunque round-trip di rete,
    // usando ESATTAMENTE lo stesso contratto strict applicato da ArtifactDemoCatalogRepository (stessa
    // funzione pura, stesse costanti di range) — mai una divergenza fra "cosa rifiuta il client" e
    // "cosa rifiuta il server", per lo stesso identico input. Sostituisce il controllo r6 (che
    // verificava solo seasonInfo/seasonKey) con la validazione completa: Id duplicati, ruolo/squadra/
    // qtA/qtI/fvm mancanti o fuori range vengono ora rifiutati QUI, non solo dalla RPC. Revisione r8
    // (audit, blocker 4): fileChecksum ora passato al validator condiviso — import_catalog_snapshot()
    // richiede già p_file_checksum non nullo/non vuoto lato server, ma prima di questa correzione
    // nessun controllo locale lo verificava PRIMA della RPC (unico fra i quattro gap residui a NON
    // avere già un fail-fast client-side per questo campo specifico sul listone).
    const contractError = validateListoneApplyContract(records, seasonInfo, fileChecksum);
    if(contractError) return { ok:false, summary:null, error:contractError };
    const rows = records.map(mapListoneRecordToRpcPayload);
    const { data, error } = await this._client.rpc("import_catalog_snapshot", {
      p_rows: rows,
      p_filename: sourceFile || "",
      p_file_checksum: fileChecksum || "",
      p_season_key: (seasonInfo && seasonInfo.seasonKey) || null,
      p_label: (seasonInfo && seasonInfo.label) || null,
      p_synthetic_test_run_id: syntheticTestRunId || null
    });
    if(error) return { ok:false, summary:null, error: mapRpcError(error) };
    return { ok:true, summary:data, error:null };
  }

  // Revisione r5 (audit, blocker 9/10/11): syntheticTestRunId OPZIONALE, mai passato dalla UI
  // applicativa — solo tests/real-supabase/test_catalog_real.js lo valorizza. Necessario perché
  // admin_purge_synthetic_test_data() non usa più season_key come guardia per la pulizia
  // dell'audit (era pericoloso: gli import di statistiche REALI hanno una season_key vera, ma
  // condividono lo stesso "kind" delle sintetiche — solo questo marker distingue in sicurezza).
  async applyStatisticsImport(preview, syntheticTestRunId){
    // Revisione r8 (audit, blocker 1): confronto ESATTO con true (mai un truthy generico) — stessa
    // guardia esplicita ora presente anche in ArtifactDemoCatalogRepository, per lo stesso identico
    // esito su preview.ok=false/0/null/undefined.
    if(!preview || preview.ok!==true) return { ok:false, summary:null, error:"Preview non valida." };
    // Revisione r7 (audit, gap di parità repository Artifact/Supabase): stesso fail-fast di
    // applyListoneSnapshot() sopra — la RPC (0008) resta il backstop autorevole, ma un input che il
    // client sa già essere invalido (Id duplicati, campi fuori range, stagione non canonica) viene
    // ora rifiutato QUI, prima del round-trip di rete, con lo STESSO esito di
    // ArtifactDemoCatalogRepository per lo stesso identico input.
    const contractError = validateStatisticsApplyContract(preview);
    if(contractError) return { ok:false, summary:null, error:contractError };
    const rows = preview.records.map(mapStatisticsRecordToRpcPayload);
    // Correzione r1 (audit): preview.season è SEMPRE un oggetto {seasonKey,label} (vedi
    // statisticsEngine.detectSeasonFromText()), mai una stringa — passarlo direttamente come
    // p_season_key (testo atteso dalla RPC) avrebbe inviato "[object Object]" a Postgres.
    const { data, error } = await this._client.rpc("import_statistics_archive", {
      p_season_key: preview.season.seasonKey, p_label: preview.season.label, p_rows: rows,
      p_filename: preview.sourceFile || "", p_file_checksum: preview.fileChecksum || "",
      p_synthetic_test_run_id: syntheticTestRunId || null
    });
    if(error) return { ok:false, summary:null, error: mapRpcError(error) };
    return { ok:true, summary:data, error:null };
  }

  // Revisione r10 (audit del gate reale, root cause): PRIMA di questa correzione questo metodo
  // eseguiva un'UNICA select() non paginata su player_season_stats, e ignorava completamente
  // `error`. Con le fixture reali (663+679=1342 righe statistiche ufficiali) il default Supabase/
  // PostgREST (max 1000 righe per risposta) troncava silenziosamente il dataset, e getCoverage()/
  // getMissingDataRows()/getPlayerSeasonStats() (che passano TUTTI da questo stesso metodo — mai
  // tre implementazioni diverse) calcolavano una copertura completamente errata su dati parziali.
  // Ora usa loadAllPlayerSeasonStatRows() (sopra), fail-closed: un errore su qualunque pagina fa
  // propagare un'eccezione esplicita invece di costruire una copertura da un dataset incompleto.
  async _loadCatalogStateForCoverage(seasonKeys){
    const players = await this.listCurrentPlayers();
    const statRows = await loadAllPlayerSeasonStatRows(this._client, seasonKeys);
    return { players, statisticsArchives: buildStatisticsArchivesFromRows(statRows), statisticsConflicts:[], ignoredMissingDataEntries:[] };
  }

  async getCoverage(seasonKeys){
    const state = await this._loadCatalogStateForCoverage(seasonKeys);
    return computeMultiseasonCoverage(state, seasonKeys);
  }

  async getMissingDataRows(seasonKeys, options){
    const state = await this._loadCatalogStateForCoverage(seasonKeys);
    return computeMissingDataRows(state, seasonKeys, options);
  }

  // Round alpha.7 (A7-P1-1, A7-P1-1D2): contratto pubblico INVARIATO (playerId = players.id UUID,
  // stessa forma di ritorno, stessa semantica null/rejection) — solo l'implementazione interna
  // cambia. PRIMA di questa revisione ogni chiamata ricaricava l'intero catalogo corrente E
  // l'intera player_season_stats della stagione richiesta solo per estrarne UNA riga (vedi
  // _loadCatalogStateForCoverage sotto) — inaccettabile per un pannello statistiche interrogato ad
  // ogni nuovo giococatore candidato durante un'asta live (A7-P1-1D2 §1). Percorso rapido: una
  // query mirata (fantacalcio_id, season_key) — coperta dall'indice della UNIQUE esistente
  // (player_season_stats_unique, 0005_catalog_schema.sql), NESSUNA nuova migration — filtrata
  // ESPLICITAMENTE su source_type='fantacalcio-ufficiale' (la stessa semantica hardcoded dalla RPC
  // di import 0008, mai un maybeSingle() lasciato ambiguo nel caso futuro di un secondo
  // source_type: A7-P1-1D2 §5). Se la riga diretta manca (giocatore con storico
  // nome/squadra cambiato — historicalAliases), si ricade sul percorso storico completo
  // (full-season load + reconciliazione per alias, A7-P1-1D2 §6) — mai perso, solo evitato quando
  // non serve.
  async getPlayerSeasonStats(playerId, seasonKey){
    const player = await this.getPlayer(playerId);
    if(!player) return null;
    if(player.fantacalcioId != null){
      const { data, error } = await this._client.from("player_season_stats")
        .select(PLAYER_SEASON_STATS_COVERAGE_COLUMNS)
        .eq("fantacalcio_id", player.fantacalcioId)
        .eq("season_key", seasonKey)
        .eq("source_type", "fantacalcio-ufficiale")
        .maybeSingle();
      if(error){
        throw new Error("Impossibile leggere player_season_stats (query mirata): " + (error.message || error));
      }
      if(data) return mapPlayerSeasonStatsRow(data);
    }
    // Percorso di fallback (nessuna riga diretta, o fantacalcioId assente): stesso comportamento
    // pre-esistente, invariato — full-season load + getPlayerSeasonStats() puro (statisticsEngine.js),
    // che include la ricerca per historicalAliases quando la sola chiave fantacalcio_id non basta.
    const state = await this._loadCatalogStateForCoverage([seasonKey]);
    return getPlayerSeasonStats(player, seasonKey, state);
  }

  async getStatisticsTargetSeasons(){
    // A differenza del demo (currentSeasonKey vive in catalogState persistito), qui la stagione
    // corrente è derivata dall'ultimo import di listone riuscito — mai una seconda fonte di verità
    // da tenere sincronizzata a mano (obiettivo 3: un solo posto per "cos'è successo, quando").
    const { data } = await this._client.from("catalog_imports").select("season_key")
      .eq("kind", "listone").eq("status", "success").order("imported_at", { ascending:false }).limit(1).maybeSingle();
    const state = { currentSeasonKey: (data && data.season_key) || null, statisticsTargetSeasonsOverride:null };
    return getStatisticsTargetSeasons(state);
  }

  async listExternalRecords(){
    // Correzione r2: TUTTE le righe di external_statistics (attive e non), mai filtrate su
    // active=true qui — la UI (scheda giocatore, sezione "record esterni risolti") deve poter
    // mostrare anche le fonti risolte ma non ancora scelte esplicitamente ("selezione richiesta",
    // regola D beta.6), esattamente come ArtifactDemoCatalogRepository.listExternalRecords()
    // (obiettivo 11: stesso contratto, stessa business logic su entrambi i backend).
    const { data, error } = await this._client.from("external_statistics").select("*");
    if(error || !data) return [];
    return data.map(mapExternalStatisticsRow);
  }
  async listUnassociatedExternalRecords(){
    const { data, error } = await this._client.from("unassociated_external_statistics").select("*");
    if(error || !data) return [];
    return data.map(mapUnassociatedExternalStatisticsRow);
  }

  // Round 2.2.0: SOLA LETTURA diretta via PostgREST, stessa RLS "select to authenticated using
  // (true)" già in vigore da 0007 per statistics_conflicts (identica a catalog_imports — nessuna
  // restrizione di riga qui, l'admin-only del round riguarda la risoluzione sotto, mai la
  // visibilità). Solo status='open', ordinati per detected_at — mai risolti/ignorati, che la UI di
  // gestione non deve mai mostrare come azionabili.
  async listOfficialStatisticsConflicts(){
    const { data, error } = await this._client.from("statistics_conflicts")
      .select("*").eq("status", "open").order("detected_at", { ascending: true });
    if(error || !data) return [];
    return data.map(mapStatisticsConflictRow);
  }

  // Round 2.2.0: UNICA mutazione ammessa sui conflitti ufficiali — sempre via RPC SECURITY DEFINER
  // (0021), mai un UPDATE diretto da questo adapter (il GRANT di 0007 non concede comunque UPDATE
  // su statistics_conflicts/player_season_stats a un client authenticated qualunque). Stesso
  // principio "mai un successo silenzioso" di ogni altra RPC admin-only in questo file: un
  // non-admin riceve l'eccezione Postgres così come sollevata dalla RPC, tradotta qui in
  // { ok:false, error }.
  async resolveStatisticsConflict(conflictId, action, customValue){
    const { data, error } = await this._client.rpc("resolve_statistics_conflict", {
      p_conflict_id: conflictId, p_action: action, p_custom_value: customValue == null ? null : customValue
    });
    if(error) return { ok:false, status:null, resolvedValue:null, error: mapRpcError(error) };
    return { ok:!!(data && data.ok), status: (data && data.status) || null, resolvedValue: (data && data.resolvedValue) != null ? data.resolvedValue : null, error: (data && data.error) || null };
  }

  // Revisione r5 (audit, blocker 9/10/11): syntheticTestRunId OPZIONALE, mai passato dalla UI
  // applicativa — solo il test reale lo valorizza, per permettere ad
  // admin_purge_synthetic_test_data() di ripulire in sicurezza l'audit di questo import (che ha
  // sempre season_key=NULL, indistinguibile per schema da un import esterno reale).
  async importExternalStatisticsCSV(text, filename, syntheticTestRunId){
    // Parsing lato client (stesso motore condiviso), scrittura via RPC (obiettivo 9: mai un INSERT
    // diretto su unassociated_external_statistics da un client authenticated qualunque — il GRANT
    // di 0007 lo impedirebbe comunque). Revisione r2 (blocker 3): identità file/checksum, stessa
    // idempotenza delle altre RPC di import — un reimport dello stesso identico CSV è un no-op,
    // mai una duplicazione né una perturbazione della selezione attiva.
    const { parseCSV, detectDelimiter, computeFileChecksum } = await import("./statisticsEngine.js");
    const { parseAndValidateExternalStatisticsCSV } = await import("./externalRecordsEngine.js");
    // Revisione r4 (audit, blocker 2): STESSA pipeline STRICT del repository demo — un valore fuori
    // range/non intero/non numerico fa fallire l'INTERO file PRIMA di chiamare la RPC (mai un clamp
    // silenzioso lato client che nasconderebbe al server un CSV realmente invalido: il server
    // riceverebbe già un valore "corretto" e non potrebbe più sapere che l'input originale non lo era).
    const parsed = parseAndValidateExternalStatisticsCSV(text, parseCSV, detectDelimiter);
    if(!parsed.ok) return { ok:false, warnings:[], error:parsed.error };
    const records = parsed.records;
    const checksum = computeFileChecksum(new TextEncoder().encode(text));
    const { data, error } = await this._client.rpc("import_external_statistics", {
      p_rows: records, p_filename: filename || "", p_file_checksum: checksum,
      p_synthetic_test_run_id: syntheticTestRunId || null
    });
    if(error) return { ok:false, warnings:[], error: mapRpcError(error) };
    // Revisione r3 (blocker 7): importId esposto anche qui (già presente nella risposta RPC) — un
    // chiamante che crea dati sintetici di test può così tracciare ESATTAMENTE quale riga di audit
    // catalog_imports ripulire in seguito, invece di doverla individuare con un'euristica.
    return { ok:true, warnings: (data && data.warnings) || [], error:null, skipped: !!(data && data.skipped), importId: data && data.importId };
  }

  // Round alpha.3-r1 (fondamenta enrichment provider-neutral): stessa RPC di
  // importExternalStatisticsCSV() sopra — import_external_statistics accetta ora, in ciascuna riga,
  // quattro campi OPZIONALI di identità/versione provider (0010_provider_observation_versioning.sql).
  // Riceve osservazioni RAW già fetchate da un ProviderAdapter (mai un'istanza dell'adapter: questo
  // repository non conosce mai trasporto/autenticazione di un provider), le mappa/valida STRICT
  // lato client PRIMA di chiamare la RPC (stesso principio "mai un clamp silenzioso che nasconda al
  // server un input realmente invalido" di importExternalStatisticsCSV), poi la RPC applica la
  // STESSA idempotenza per file_checksum (qui: checksum del batch di osservazioni).
  // Revisione alpha.3-r4 (blocker 5, terzo audit): checksum PROVIDER-SCOPED, stesso principio di
  // ArtifactDemoCatalogRepository — prima calcolato SOLO sul payload raw, quindi due vendor con lo
  // stesso JSON avrebbero condiviso la stessa identità di import (idempotenza server-side rispetta
  // questo stesso scoping, dato che import_external_statistics() deduplica per file_checksum, mai
  // per contenuto — vedi 0010). p_filename usa ora il providerKey CANONICO (mai il grezzo).
  async importProviderObservations(rawObservations, providerKey, syntheticTestRunId){
    const { computeFileChecksum } = await import("./statisticsEngine.js");
    const { mapAndValidateProviderObservations } = await import("./providerEnrichmentEngine.js");
    const canonicalProviderKey = canonicalizeProviderKey(providerKey);
    const mapped = mapAndValidateProviderObservations(rawObservations, providerKey);
    if(!mapped.ok) return { ok:false, warnings:[], error:mapped.error };
    const checksum = computeFileChecksum(new TextEncoder().encode(JSON.stringify({ providerKey: canonicalProviderKey, observations: rawObservations || [] })));
    const { data, error } = await this._client.rpc("import_external_statistics", {
      p_rows: mapped.records, p_filename: "provider:" + canonicalProviderKey, p_file_checksum: checksum,
      p_synthetic_test_run_id: syntheticTestRunId || null
    });
    if(error) return { ok:false, warnings:[], error: mapRpcError(error) };
    return { ok:true, warnings: (data && data.warnings) || [], error:null, skipped: !!(data && data.skipped), importId: data && data.importId };
  }

  // Revisione alpha.3-r3 (0010, audit blocker B): `providerKey` (opzionale, quinto parametro — mai
  // rompe una chiamata esistente a 4 argomenti) seleziona quale vendor per una fonte vendor-scoped
  // (oggi solo "provider-authorized"). Stesso backstop client-side di ArtifactDemoCatalogRepository
  // (mai fidarsi solo del server, stesso principio del resto di questo file) — la RPC (0010, una
  // volta applicata) applica comunque il proprio controllo server-side indipendente.
  async associateExternalId(playerId, source, sourcePlayerId, sourceUrl, providerKey){
    const vendorScoped = isVendorScopedExternalSource(source);
    const canonicalProviderKey = providerKey==null ? null : canonicalizeProviderKey(providerKey);
    if(!vendorScoped && canonicalProviderKey){
      return { ok:false, error:"providerKey non è consentito per la fonte \""+source+"\" (solo per fonti vendor-scoped)." };
    }
    if(vendorScoped){
      if(!canonicalProviderKey) return { ok:false, error:"providerKey obbligatorio per un'associazione \""+source+"\" (identità vendor-scoped)." };
      const providerKeyErr = validateProviderKeyFormat(canonicalProviderKey);
      if(providerKeyErr) return { ok:false, error:providerKeyErr };
    }
    const { data, error } = await this._client.rpc("associate_external_id", {
      p_player_id: playerId, p_source: source, p_source_player_id: sourcePlayerId, p_source_url: sourceUrl || "",
      p_provider_key: canonicalProviderKey
    });
    if(error) return { ok:false, error: mapRpcError(error) };
    return { ok:true, error:null, ...(data||{}) };
  }

  async removeExternalId(playerId, source, providerKey){
    const canonicalProviderKey = providerKey==null ? null : canonicalizeProviderKey(providerKey);
    const { data, error } = await this._client.rpc("remove_external_id", { p_player_id: playerId, p_source: source, p_provider_key: canonicalProviderKey });
    if(error) return { ok:false, error: mapRpcError(error) };
    return { ok:true, error:null, ...(data||{}) };
  }

  async setActiveExternalSource(recordId){
    const { data, error } = await this._client.rpc("set_active_external_source", { p_record_id: recordId });
    if(error) return { ok:false, error: mapRpcError(error) };
    return { ok:true, error:null, ...(data||{}) };
  }
}
